import { clearTokens } from '@/api/apiClient'
import { useUserStore } from '@/stores/user'
import {
  createRouter,
  createWebHistory,
  type RouteRecordNameGeneric,
  type RouteRecordRaw,
} from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'

export enum ROUTE_NAMES {
  HOME = 'home',
  LOGIN = 'login',
  REGISTER = 'register',
  REGISTER_AFTER_MESSAGE = 'register-after-message',
  REQUEST_PASSWORD_RESET = 'request-password-reset',
  PASSWORD_RESET = 'password-reset',
  DASHBOARD = 'dashboard',
  DEACTIVATED_USER_DASHBOARD = 'deactivated-user-dashboard',
  GOOGLE_LOGIN_CALLBACK = 'google-login-callback',

  // Support
  SUPPORT = 'support',
  SUPPORT_OLD = 'support-old',
  SUPPORT_NEW_CHAT = 'support-new-chat',
  SUPPORT_CHAT = 'support-chat',

  // Shipments
  SHIPMENTS = 'shipments',

  // Profile
  PROFILE = 'profile',
  PROFILE_INFO = 'profile-info',
  PROFILE_EMAIL_NOTIFICATIONS = 'profile-email-notifications',
  PROFILE_PRODUCTS = 'profile-products',
  PROFILE_REQUEST_PASSWORD_RESET = 'profile-request-password-reset',
  PROFILE_PASSWORD_RESET = 'profile-password-reset',

  // Products
  PRODUCT_LIST = 'product-list',
  VIEW_PRODUCT = 'view-product',
  CREATE_PRODUCT = 'create-product',
  EDIT_PRODUCT = 'edit-product',

  // Inbound shipments
  INBOUND_SHIPMENT_LIST = 'inbound-shipment-list',
  CREATE_INBOUND_SHIPMENT_DRAFT = 'create-inbound-shipment-draft',
  EDIT_INBOUND_SHIPMENT_DRAFT = 'edit-inbound-shipment-draft',
  EDIT_INBOUND_SHIPMENT = 'edit-inbound-shipment',
  VIEW_INBOUND_SHIPMENT = 'view-inbound-shipment',
  CREATE_RETURN_SHIPMENT = 'create-return-shipment',

  // Return shipments
  RETURN_SHIPMENT_LIST = 'return-shipment-list',
  VIEW_RETURN_SHIPMENT = 'view-return-shipment',

  // Forwarding shipments
  FORWARDING_SHIPMENT_LIST = 'forwarding-shipment-list',
  VIEW_FORWARDING_SHIPMENT = 'view-forwarding-shipment',

  // Sales channels
  SALES_CHANNELS = 'sales-channels',
  SALES_CONNECT_EBAY = 'connect-ebay',
  SALES_CONNECT_EBAY_SUCCESS = 'connect-ebay-success',
  SALES_CONNECT_EBAY_FAILURE = 'connect-ebay-failure',
  SALES_CONNECT_ETSY = 'connect-etsy',
  SALES_CONNECT_ETSY_REDIRECTED = 'connect-etsy-redirected',
  SALES_CONNECT_AMAZON = 'connect-amazon',
  SALES_CONNECT_SHOPIFY = 'connect-shopify',
  SALES_CONNECT_WALMART = 'connect-walmart',

  // Orders
  ORDER_LIST = 'order-list',
  ORDER_LIST_ACTION_REQUIRED = 'order-list-action-required',
  ORDER_LIST_PROCESSING = 'order-list-processing',
  ORDER_LIST_COMPLETED = 'order-list-completed',
  ORDER_LIST_CANCELLED = 'order-list-cancelled',
  ORDER_LIST_ALL = 'order-list-all',
  CREATE_ORDER = 'create-order',
  EDIT_ORDER = 'edit-order',
  VIEW_ORDER = 'view-order',
  VIEW_ORDER_ACTION_REQUIRED = 'view-order-action-required',
  VIEW_ORDER_PROCESSING = 'view-order-processing',
  VIEW_ORDER_COMPLETED = 'view-order-completed',
  VIEW_ORDER_CANCELLED = 'view-order-cancelled',

  // Payments
  PAYMENT_INFORMATION = 'payment-information',
  WALLET = 'wallet',
  WALLET_TOPUPS = 'wallet-topups',
  WALLET_SPENDINGS = 'wallet-spendings',
  SUBSCRIPTION = 'subscription',
  SUBSCRIPTION_CHANGE = 'subscription-change',
  ADD_YOOKASSA_CARD = 'add-yookass-card',

  // Admin
  ADMIN = 'admin',
  ADMIN_USERS = 'admin-users',
  ADMIN_DEBTORS = 'admin-debtors',
  ADMIN_USER_VIEW = 'admin-user-view',
  ADMIN_USER_INFO_VIEW = 'admin-user-info-view',
  ADMIN_USER_WALLET_VIEW = 'admin-user-wallet-view',
  ADMIN_USER_DEBT_VIEW = 'admin-user-debt-view',
  ADMIN_USER_SUBSCRIPTION_VIEW = 'admin-user-subscription-view',
  ADMIN_USER_SALES_CHANNELS_VIEW = 'admin-user-sales-channels-view',
  ADMIN_USER_PRODUCTS_VIEW = 'admin-user-products-view',

  ADMIN_WAREHOUSE = 'admin-warehouse',
  ADMIN_INBOUND_SHIPMENTS = 'admin-inbound-shipments',
  ADMIN_VIEW_SHIPMENT = 'admin-view-shipment',
  ADMIN_CHECKING_SHIPMENT = 'admin-checking-shipment',
  ADMIN_PLACEMENT_SHIPMENT = 'admin-placement-shipment',

  ADMIN_FULFILLMENT_LIST = 'admin-fulfillment-list',
  ADMIN_FULFILLMENT_VIEW = 'admin-fulfillment-view',
  ADMIN_FULFILLMENT_PICKING = 'admin-fulfillment-picking',

  ADMIN_FORWARDING_SHIPMENT_LIST = 'admin-forwarding-shipment-list',
  ADMIN_FORWARDING_SHIPMENT_VIEW = 'admin-forwarding-shipment-view',

  ADMIN_PRODUCTS = 'admin-products',
  ADMIN_PRODUCT_VIEW = 'admin-product-view',
  ADMIN_PRODUCT_INVENTORY_CHANGE_HISTORY = 'admin-product-inventory-change-history',
  ADMIN_PRODUCT_RESERVED_STOCK_CHANGE_HISTORY = 'admin-product-reserved-stock-change-history',

  ADMIN_MANIFESTS = 'admin-manifests',
  ADMIN_MANIFESTS_ACTIVE = 'admin-manifests-active',
  ADMIN_MANIFESTS_ARCHIVE = 'admin-manifests-archive',
  ADMIN_CREATE_MANIFEST = 'admin-create-manifest',
  ADMIN_EDIT_MANIFEST = 'admin-edit-manifest',
  ADMIN_VIEW_MANIFEST = 'admin-view-manifest',

  ADMIN_SUPPORT = 'admin-support',
  ADMIN_SUPPORT_USER_INITIATED = 'admin-support-user-initiated',
  ADMIN_SUPPORT_USER_INITIATED_CHAT = 'admin-support-user-initiated-chat',
  ADMIN_SUPPORT_SYSTEM_INITIATED = 'admin-support-system-initiated',
  ADMIN_SUPPORT_SYSTEM_INITIATED_CHAT = 'admin-support-system-initiated-chat',
  ADMIN_SUPPORT_NEW_USER_CHAT = 'admin-support-new-user-chat',
  ADMIN_SUPPORT_NEW_SYSTEM_CHAT = 'admin-support-new-system-chat',

  ADMIN_PROMOCODES = 'admin-promo-codes',
  ADMIN_PROMOCODE_VIEW = 'admin-promo-codes-view',

  ADMIN_IMPERSONATED_MODE_SHARE = 'admin-impersonated-mode-share',
  //smth for test
  TEST = 'test',
  TEST_TABLE = 'test-table',
}

const clearUserInfoMiddleware = () => {
  const user = useUserStore()
  const queryClient = useQueryClient()

  user.clearUser()
  user.setImpersonatedUser(null)
  clearTokens()

  queryClient.invalidateQueries({ refetchType: 'none' })

  // Sentry.setUser(null)

  return true
}

const routes: RouteRecordRaw[] = [
  // Authorized pages
  {
    path: '/',
    redirect: { name: ROUTE_NAMES.DASHBOARD },
    beforeEnter: (to) => {
      const { isAuthenticated, originalUser } = useUserStore()

      if (!isAuthenticated) {
        return { name: ROUTE_NAMES.LOGIN, query: { redirectTo: to.fullPath || undefined } }
      }

      if (originalUser?.user_type === 'WAREHOUSE_MANAGER') {
        return { name: ROUTE_NAMES.ADMIN_WAREHOUSE }
      }
      return true
    },
    component: () => import('../components/layouts/LayoutAuthorized.vue'),

    children: [
      {
        path: 'dashboard',
        name: ROUTE_NAMES.DASHBOARD,
        // component: () => import('../views/DashboardView.vue'),
        redirect: { name: ROUTE_NAMES.ORDER_LIST },
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
            },
          ],
        },
      },
      {
        path: 'deactivated',
        name: ROUTE_NAMES.DEACTIVATED_USER_DASHBOARD,
        component: () => import('../views/DeactivatedUserDashboardView.vue'),
        beforeEnter: () => {
          const { user } = useUserStore()
          if (!user?.blocked) {
            return { name: ROUTE_NAMES.DASHBOARD }
          }
          return true
        },
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
            },
          ],
        },
      },
      {
        path: 'support',
        name: ROUTE_NAMES.SUPPORT,
        component: () => import('../views/support/SupportView.vue'),
        redirect: { name: ROUTE_NAMES.SUPPORT_NEW_CHAT },
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'support',
            },
          ],
        },
        children: [
          {
            path: 'new-chat',
            name: ROUTE_NAMES.SUPPORT_NEW_CHAT,
            component: () => import('../views/support/SupportNewChatView.vue'),
          },
          {
            path: ':chatId',
            name: ROUTE_NAMES.SUPPORT_CHAT,
            component: () => import('../views/support/SupportChatView.vue'),
          },
        ],
      },
      {
        path: 'profile',
        name: ROUTE_NAMES.PROFILE,
        component: () => import('../views/profile/ProfileView.vue'),
        redirect: { name: ROUTE_NAMES.PROFILE_INFO },
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'profile',
            },
          ],
        },
        children: [
          {
            path: 'info',
            name: ROUTE_NAMES.PROFILE_INFO,
            component: () => import('../views/profile/ProfileView/ProfileInfoView.vue'),
          },
          {
            path: 'email-settings',
            name: ROUTE_NAMES.PROFILE_EMAIL_NOTIFICATIONS,
            component: () =>
              import('../views/profile/ProfileView/ProfileEmailNotificationsView.vue'),
          },
          {
            path: 'products',
            name: ROUTE_NAMES.PROFILE_PRODUCTS,
            component: () => import('../views/profile/ProfileView/ProfileProductsView.vue'),
          },
        ],
      },
      {
        path: '/profile-request-password-reset',
        name: ROUTE_NAMES.PROFILE_REQUEST_PASSWORD_RESET,
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'profile',
              to: { name: ROUTE_NAMES.PROFILE },
            },
            {
              textKey: 'reset_password',
            },
          ],
        },
        component: () => import('../views/profile/RequestPasswordResetView.vue'),
      },
      {
        path: '/profile-password-reset',
        name: ROUTE_NAMES.PROFILE_PASSWORD_RESET,
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'profile',
              to: { name: ROUTE_NAMES.PROFILE },
            },
            {
              textKey: 'reset_password',
            },
          ],
        },
        component: () => import('../views/profile/PasswordResetView.vue'),
      },

      // Products
      {
        path: 'products',
        name: ROUTE_NAMES.PRODUCT_LIST,
        component: () => import('../views/products/ProductListView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'products',
            },
          ],
        },
        children: [
          {
            path: ':productId',
            name: ROUTE_NAMES.VIEW_PRODUCT,
            meta: {
              isModal: true,
            },
            component: () => import('../views/products/ProductView.vue'),
          },
          {
            path: 'create',
            name: ROUTE_NAMES.CREATE_PRODUCT,
            meta: {
              isModal: true,
            },
            component: () => import('../views/products/CreateProductView.vue'),
          },
        ],
      },
      {
        path: 'products/:productId/edit',
        name: ROUTE_NAMES.EDIT_PRODUCT,
        component: () => import('../views/products/EditProductView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'products',
              to: { name: ROUTE_NAMES.PRODUCT_LIST },
            },
            {
              textKey: 'edit_product',
            },
          ],
        },
      },

      // Shipments
      {
        path: 'shipments',
        name: ROUTE_NAMES.SHIPMENTS,
        component: () => import('../views/ShipmentsView.vue'),
        redirect: { name: ROUTE_NAMES.INBOUND_SHIPMENT_LIST },
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'shipments',
            },
          ],
        },
        children: [
          {
            path: 'inbound',
            name: ROUTE_NAMES.INBOUND_SHIPMENT_LIST,
            component: () => import('../views/inbound-shipments/InboundShipmentListView.vue'),
            children: [
              {
                path: ':shipmentId',
                name: ROUTE_NAMES.VIEW_INBOUND_SHIPMENT,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/inbound-shipments/InboundShipmentView.vue'),
              },
            ],
          },

          // Forwarding shipments
          {
            path: 'forwarding-shipments',
            name: ROUTE_NAMES.FORWARDING_SHIPMENT_LIST,
            component: () => import('../views/forwarding-shipments/ForwardingShipmentListView.vue'),
            children: [
              {
                path: ':shipmentId',
                name: ROUTE_NAMES.VIEW_FORWARDING_SHIPMENT,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/forwarding-shipments/ForwardingShipmentView.vue'),
              },
            ],
          },

          // Return shipments
          {
            path: 'return',
            name: ROUTE_NAMES.RETURN_SHIPMENT_LIST,
            component: () => import('../views/return-shipments/ReturnShipmentListView.vue'),
            children: [
              {
                path: ':shipmentId',
                name: ROUTE_NAMES.VIEW_RETURN_SHIPMENT,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/return-shipments/ReturnShipmentView.vue'),
              },
            ],
          },
        ],
      },

      // Inbound shipments
      {
        path: 'create-inbound-shipment',
        name: ROUTE_NAMES.CREATE_INBOUND_SHIPMENT_DRAFT,
        component: () => import('../views/inbound-shipments/CreateInboundShipmentDraft.vue'),
      },
      {
        path: 'edit-inbound-shipment-draft/:id',
        name: ROUTE_NAMES.EDIT_INBOUND_SHIPMENT_DRAFT,
        component: () => import('../views/inbound-shipments/EditInboundShipmentDraft.vue'),
      },
      {
        path: 'edit-inbound-shipment/:id',
        name: ROUTE_NAMES.EDIT_INBOUND_SHIPMENT,
        component: () => import('../views/inbound-shipments/EditInboundShipment.vue'),
      },
      {
        path: 'create-return-shipment',
        name: ROUTE_NAMES.CREATE_RETURN_SHIPMENT,
        component: () => import('../views/return-shipments/CreateReturnShipment.vue'),
      },

      // Sales channels
      {
        path: 'sales-channels',
        name: ROUTE_NAMES.SALES_CHANNELS,
        component: () => import('../views/sales-channels/SalesChannelsView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'sales_channels',
            },
          ],
        },
      },
      {
        path: 'sales-channels/connect-ebay',
        name: ROUTE_NAMES.SALES_CONNECT_EBAY,
        component: () => import('../views/sales-channels/SalesConnectEbay.vue'),
      },
      {
        path: 'marketplaces/ebay/new/success',
        name: ROUTE_NAMES.SALES_CONNECT_EBAY_SUCCESS,
        component: () => import('../views/sales-channels/SalesConnectEbaySuccess.vue'),
      },
      {
        path: 'marketplaces/ebay/new/failure',
        name: ROUTE_NAMES.SALES_CONNECT_EBAY_FAILURE,
        component: () => import('../views/sales-channels/SalesConnectEbayFailure.vue'),
      },
      {
        path: 'sales-channels/connect-amazon',
        name: ROUTE_NAMES.SALES_CONNECT_AMAZON,
        component: () => import('../views/sales-channels/SalesConnectAmazon.vue'),
      },
      {
        path: 'sales-channels/connect-etsy',
        name: ROUTE_NAMES.SALES_CONNECT_ETSY,
        component: () => import('../views/sales-channels/SalesConnectEtsy.vue'),
      },
      {
        path: 'sales_channel/connect/oauth_return/etsy',
        name: ROUTE_NAMES.SALES_CONNECT_ETSY_REDIRECTED,
        component: () => import('../views/sales-channels/SalesConnectEtsyRedirected.vue'),
      },
      {
        path: 'sales-channels/connect-shopify',
        name: ROUTE_NAMES.SALES_CONNECT_SHOPIFY,
        component: () => import('../views/sales-channels/SalesConnectShopify.vue'),
      },
      {
        path: 'sales-channels/connect-walmart',
        name: ROUTE_NAMES.SALES_CONNECT_WALMART,
        component: () => import('../views/sales-channels/SalesConnectWalmart.vue'),
      },

      // Orders
      {
        path: 'orders',
        name: ROUTE_NAMES.ORDER_LIST,
        component: () => import('../views/orders/OrderListView.vue'),
        redirect: { name: ROUTE_NAMES.ORDER_LIST_ALL },
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'orders',
            },
          ],
        },
        children: [
          {
            path: 'action-required',
            name: ROUTE_NAMES.ORDER_LIST_ACTION_REQUIRED,
            component: () => import('../views/orders/OrderListTableView.vue'),
            children: [
              {
                path: ':orderId',
                name: ROUTE_NAMES.VIEW_ORDER_ACTION_REQUIRED,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/orders/OrderView.vue'),
              },
            ],
          },
          {
            path: 'processing',
            name: ROUTE_NAMES.ORDER_LIST_PROCESSING,
            component: () => import('../views/orders/OrderListTableView.vue'),
            children: [
              {
                path: ':orderId',
                name: ROUTE_NAMES.VIEW_ORDER_PROCESSING,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/orders/OrderView.vue'),
              },
            ],
          },
          {
            path: 'completed',
            name: ROUTE_NAMES.ORDER_LIST_COMPLETED,
            component: () => import('../views/orders/OrderListTableView.vue'),
            children: [
              {
                path: ':orderId',
                name: ROUTE_NAMES.VIEW_ORDER_COMPLETED,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/orders/OrderView.vue'),
              },
            ],
          },
          {
            path: 'cancelled',
            name: ROUTE_NAMES.ORDER_LIST_CANCELLED,
            component: () => import('../views/orders/OrderListTableView.vue'),
            children: [
              {
                path: ':orderId',
                name: ROUTE_NAMES.VIEW_ORDER_CANCELLED,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/orders/OrderView.vue'),
              },
            ],
          },
          {
            path: 'all',
            name: ROUTE_NAMES.ORDER_LIST_ALL,
            component: () => import('../views/orders/OrderListTableView.vue'),
            children: [
              {
                path: ':orderId',
                name: ROUTE_NAMES.VIEW_ORDER,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/orders/OrderView.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'orders/create',
        name: ROUTE_NAMES.CREATE_ORDER,
        component: () => import('../views/orders/CreateOrderView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'orders',
              to: { name: ROUTE_NAMES.ORDER_LIST },
            },
            {
              textKey: 'new_order',
            },
          ],
        },
      },
      {
        path: 'orders/:orderId/edit',
        name: ROUTE_NAMES.EDIT_ORDER,
        component: () => import('../views/orders/EditOrderView.vue'),
        meta: {
          mandatoryScrollTop: true,
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'orders',
              to: { name: ROUTE_NAMES.ORDER_LIST },
            },
            {
              textKey: 'edit_order',
            },
          ],
        },
      },

      // Payments
      {
        path: 'payment-information',
        name: ROUTE_NAMES.PAYMENT_INFORMATION,
        component: () => import('../views/billing/PaymentInfoView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'payment_information',
            },
          ],
        },
      },
      {
        path: 'wallet',
        name: ROUTE_NAMES.WALLET,
        component: () => import('../views/billing/WalletView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'wallet',
            },
          ],
        },
        children: [
          {
            name: ROUTE_NAMES.WALLET_TOPUPS,
            path: 'topups',
            component: () => import('../views/billing/WalletTopupsView.vue'),
            meta: {
              noScroll: true,
            },
          },
          {
            name: ROUTE_NAMES.WALLET_SPENDINGS,
            path: 'spendings',
            component: () => import('../views/billing/WalletSpendingsView.vue'),
            meta: {
              noScroll: true,
            },
          },
        ],
        redirect: { name: ROUTE_NAMES.WALLET_TOPUPS },
      },
      {
        path: 'subscription',
        name: ROUTE_NAMES.SUBSCRIPTION,
        component: () => import('../views/billing/SubscriptionView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'subscription',
            },
          ],
        },
      },
      {
        path: 'subscription-change',
        name: ROUTE_NAMES.SUBSCRIPTION_CHANGE,
        component: () => import('../views/billing/SubscriptionChangeView.vue'),
        meta: {
          breadcrumbs: [
            {
              textKey: 'home',
              to: { name: ROUTE_NAMES.DASHBOARD },
            },
            {
              textKey: 'subscription',
              to: { name: ROUTE_NAMES.SUBSCRIPTION },
            },
            {
              textKey: 'subscription_change',
            },
          ],
        },
      },

      {
        path: '/add-yookassa-card',
        name: ROUTE_NAMES.ADD_YOOKASSA_CARD,
        component: () => import('../views/billing/AddYookassaCardView.vue'),
      },
      {
        path: '/stripe_card_setup',
        redirect: { name: ROUTE_NAMES.PAYMENT_INFORMATION },
      },
      {
        path: '/stripe_payment',
        redirect: { name: ROUTE_NAMES.WALLET },
      },
      {
        path: '/yookassa_card_setup',
        redirect: { name: ROUTE_NAMES.PAYMENT_INFORMATION },
      },
      {
        path: '/yookassa_payment',
        redirect: { name: ROUTE_NAMES.WALLET },
      },

      // Tests
      // {
      //   path: 'test-page',
      //   name: ROUTE_NAMES.TEST,
      //   component: () => import('../views/TestView.vue'),
      // },
      {
        path: '/test-table',
        name: ROUTE_NAMES.TEST_TABLE,
        component: () => import('../views/TestTableView.vue'),
      },
    ],
  },
  // Tests
  {
    path: '/test-page',
    name: ROUTE_NAMES.TEST,
    component: () => import('../views/TestView.vue'),
  },
  {
    path: '/admin',
    redirect: { name: ROUTE_NAMES.ADMIN_WAREHOUSE },
    name: ROUTE_NAMES.ADMIN,
    beforeEnter: (to) => {
      const userStore = useUserStore()

      if (!userStore.isAuthenticated) {
        return { name: ROUTE_NAMES.LOGIN, query: { redirectTo: to.fullPath || undefined } }
      }

      const queryClient = useQueryClient()

      queryClient.invalidateQueries({ refetchType: 'none' })

      if (
        userStore.originalUser?.user_type === 'WAREHOUSE_MANAGER' ||
        userStore.originalUser?.user_type === 'STAFF'
      ) {
        return true
      }
      return { name: ROUTE_NAMES.DASHBOARD }
    },
    component: () => import('../components/layouts/LayoutAdmin.vue'),
    children: [
      {
        path: 'users',
        name: ROUTE_NAMES.ADMIN_USERS,
        component: () => import('../views/admin/Users/AdminUserListView.vue'),
      },
      {
        path: 'debtors',
        name: ROUTE_NAMES.ADMIN_DEBTORS,
        component: () => import('../views/admin/Users/AdminDebtorListView.vue'),
      },
      {
        path: 'users/:userId',
        name: ROUTE_NAMES.ADMIN_USER_VIEW,
        component: () => import('../views/admin/Users/AdminUserView.vue'),
        redirect: { name: ROUTE_NAMES.ADMIN_USER_INFO_VIEW },
        children: [
          {
            name: ROUTE_NAMES.ADMIN_USER_INFO_VIEW,
            component: () => import('../views/admin/Users/AdminUserView/AdminUserInfoView.vue'),
            path: 'user-info',
          },
          {
            name: ROUTE_NAMES.ADMIN_USER_WALLET_VIEW,
            component: () => import('../views/admin/Users/AdminUserView/AdminUserWalletView.vue'),
            path: 'wallet',
          },
          {
            name: ROUTE_NAMES.ADMIN_USER_DEBT_VIEW,
            component: () => import('../views/admin/Users/AdminUserView/AdminUserDebtView.vue'),
            path: 'debt',
          },
          {
            name: ROUTE_NAMES.ADMIN_USER_SUBSCRIPTION_VIEW,
            component: () =>
              import('../views/admin/Users/AdminUserView/AdminUserSubscriptionView.vue'),
            path: 'subscription',
          },
          {
            name: ROUTE_NAMES.ADMIN_USER_SALES_CHANNELS_VIEW,
            component: () =>
              import('../views/admin/Users/AdminUserView/AdminUserSalesChannelsView.vue'),
            path: 'sales-channels',
          },
          {
            name: ROUTE_NAMES.ADMIN_USER_PRODUCTS_VIEW,
            component: () => import('../views/admin/Users/AdminUserView/AdminUserProductsView.vue'),
            path: 'products',
          },
        ],
      },
      {
        path: 'warehouse',
        name: ROUTE_NAMES.ADMIN_WAREHOUSE,
        component: () => import('../views/admin/AdminWarehouseView.vue'),
        redirect: { name: ROUTE_NAMES.ADMIN_INBOUND_SHIPMENTS },
        children: [
          {
            path: 'inbound-shipments',
            name: ROUTE_NAMES.ADMIN_INBOUND_SHIPMENTS,
            component: () => import('../views/admin/InboundShipments/AdminShipmentListView.vue'),
            children: [
              {
                path: ':shipmentId',
                name: ROUTE_NAMES.ADMIN_VIEW_SHIPMENT,
                meta: {
                  isModal: true,
                },
                component: () =>
                  import('../views/admin/InboundShipments/AdminInboundShipmentsViewShipment.vue'),
              },
            ],
          },
          {
            path: 'fulfillment',
            name: ROUTE_NAMES.ADMIN_FULFILLMENT_LIST,
            component: () => import('../views/admin/Fulfillment/AdminFulfillmentListView.vue'),
            children: [
              {
                path: ':fulfillmentId',
                name: ROUTE_NAMES.ADMIN_FULFILLMENT_VIEW,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/admin/Fulfillment/AdminFulfillmentView.vue'),
              },
            ],
          },
          {
            path: 'forwarding-shipments',
            name: ROUTE_NAMES.ADMIN_FORWARDING_SHIPMENT_LIST,
            component: () =>
              import('../views/admin/ForwardingShipments/AdminForwardingShipmentListView.vue'),
            children: [
              {
                path: ':shipmentId',
                name: ROUTE_NAMES.ADMIN_FORWARDING_SHIPMENT_VIEW,
                meta: {
                  isModal: true,
                },
                component: () =>
                  import('../views/admin/ForwardingShipments/AdminForwardingShipmentView.vue'),
              },
            ],
          },
          {
            path: 'products',
            name: ROUTE_NAMES.ADMIN_PRODUCTS,
            component: () => import('../views/admin/Products/AdminProductsView.vue'),
            children: [
              {
                path: ':productId',
                name: ROUTE_NAMES.ADMIN_PRODUCT_VIEW,
                meta: {
                  isModal: true,
                },
                component: () => import('../views/admin/Products/AdminProductView.vue'),
                redirect: { name: ROUTE_NAMES.ADMIN_PRODUCT_INVENTORY_CHANGE_HISTORY },
                children: [
                  {
                    path: 'inventory-change-history',
                    name: ROUTE_NAMES.ADMIN_PRODUCT_INVENTORY_CHANGE_HISTORY,
                    component: () =>
                      import(
                        '../views/admin/Products/AdminProductView/AdminInventoryChangeHistory.vue'
                      ),
                  },
                  {
                    path: 'reserved-stock-change-history',
                    name: ROUTE_NAMES.ADMIN_PRODUCT_RESERVED_STOCK_CHANGE_HISTORY,
                    component: () =>
                      import(
                        '../views/admin/Products/AdminProductView/AdminReservedStockChangeHistory.vue'
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'manifests',
            name: ROUTE_NAMES.ADMIN_MANIFESTS,
            component: () => import('../views/admin/Manifests/AdminManifestsView.vue'),
            redirect: { name: ROUTE_NAMES.ADMIN_MANIFESTS_ACTIVE },
            children: [
              {
                path: 'active',
                name: ROUTE_NAMES.ADMIN_MANIFESTS_ACTIVE,
                component: () => import('../views/admin/Manifests/AdminManifestsTableView.vue'),
              },
              {
                path: 'archive',
                name: ROUTE_NAMES.ADMIN_MANIFESTS_ARCHIVE,
                component: () => import('../views/admin/Manifests/AdminManifestsTableView.vue'),
              },
            ],
          },
          {
            path: 'manifests/create',
            name: ROUTE_NAMES.ADMIN_CREATE_MANIFEST,
            component: () => import('../views/admin/Manifests/AdminCreateManifestView.vue'),
          },
          {
            path: 'manifests/:manifestId/edit',
            name: ROUTE_NAMES.ADMIN_EDIT_MANIFEST,
            component: () => import('../views/admin/Manifests/AdminEditManifestView.vue'),
          },
          {
            path: 'manifests/:manifestId',
            name: ROUTE_NAMES.ADMIN_VIEW_MANIFEST,
            component: () => import('../views/admin/Manifests/AdminManifestView.vue'),
          },
        ],
      },
      {
        path: 'support',
        name: ROUTE_NAMES.ADMIN_SUPPORT,
        component: () => import('../views/admin/Support/AdminSupportView.vue'),
        redirect: { name: ROUTE_NAMES.ADMIN_SUPPORT_USER_INITIATED },
        children: [
          {
            path: 'user-initiated',
            name: ROUTE_NAMES.ADMIN_SUPPORT_USER_INITIATED,
            component: () => import('../views/admin/Support/AdminSupportTabView.vue'),
            redirect: { name: ROUTE_NAMES.ADMIN_SUPPORT_NEW_USER_CHAT },
            children: [
              {
                path: 'new',
                name: ROUTE_NAMES.ADMIN_SUPPORT_NEW_USER_CHAT,
                component: () => import('../views/admin/Support/AdminSupportNewUserChat.vue'),
              },
              {
                path: ':chatId',
                name: ROUTE_NAMES.ADMIN_SUPPORT_USER_INITIATED_CHAT,
                component: () => import('../views/admin/Support/AdminSupportChatView.vue'),
              },
            ],
          },
          {
            path: 'system-initiated',
            name: ROUTE_NAMES.ADMIN_SUPPORT_SYSTEM_INITIATED,
            redirect: { name: ROUTE_NAMES.ADMIN_SUPPORT_NEW_SYSTEM_CHAT },
            component: () => import('../views/admin/Support/AdminSupportTabView.vue'),
            children: [
              {
                path: 'new',
                name: ROUTE_NAMES.ADMIN_SUPPORT_NEW_SYSTEM_CHAT,
                component: () => import('../views/admin/Support/AdminSupportNewSystemChat.vue'),
              },
              {
                path: ':chatId',
                name: ROUTE_NAMES.ADMIN_SUPPORT_SYSTEM_INITIATED_CHAT,
                component: () => import('../views/admin/Support/AdminSupportChatView.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'promocodes',
        name: ROUTE_NAMES.ADMIN_PROMOCODES,
        component: () => import('../views/admin/Promocodes/AdminPromocodesView.vue'),
        children: [
          {
            path: ':promocodeId',
            name: ROUTE_NAMES.ADMIN_PROMOCODE_VIEW,
            component: () => import('../views/admin/Promocodes/AdminPromocodeView.vue'),
            meta: {
              isModal: true,
            },
          },
        ],
      },
      {
        path: 'checking-inbound-shipment/:shipmentId',
        name: ROUTE_NAMES.ADMIN_CHECKING_SHIPMENT,
        component: () => import('../views/admin/InboundShipments/AdminCheckingShipmentView.vue'),
      },
      {
        path: 'inbound-shipment-placement/:shipmentId',
        name: ROUTE_NAMES.ADMIN_PLACEMENT_SHIPMENT,
        component: () => import('../views/admin/InboundShipments/AdminPlacementShipmentView.vue'),
      },
      {
        path: 'fulfillment-picking',
        name: ROUTE_NAMES.ADMIN_FULFILLMENT_PICKING,
        component: () => import('../views/admin/Fulfillment/AdminFulfillmentPickingView.vue'),
      },
      {
        path: 'impersonated-mode-share-link',
        name: ROUTE_NAMES.ADMIN_IMPERSONATED_MODE_SHARE,
        component: () => import('../views/admin/AdminImpersonatedModeShareView.vue'),
      },
    ],
  },
  // Unauthorized pages
  {
    path: '/',
    redirect: { name: ROUTE_NAMES.LOGIN },
    component: () => import('../components/layouts/LayoutUnauthorized.vue'),
    children: [
      {
        path: '/login',
        name: ROUTE_NAMES.LOGIN,
        beforeEnter: clearUserInfoMiddleware,
        component: () => import('../views/auth/LoginView.vue'),
      },
      {
        path: '/registration',
        name: ROUTE_NAMES.REGISTER,
        beforeEnter: clearUserInfoMiddleware,
        component: () => import('../views/auth/RegisterView.vue'),
      },
      {
        path: '/registration-confirm-email',
        name: ROUTE_NAMES.REGISTER_AFTER_MESSAGE,
        beforeEnter: clearUserInfoMiddleware,
        component: () => import('../views/auth/RegisterAfterMessage.vue'),
      },
      {
        path: '/request-password-reset',
        name: ROUTE_NAMES.REQUEST_PASSWORD_RESET,
        beforeEnter: clearUserInfoMiddleware,
        component: () => import('../views/auth/RequestPasswordResetView.vue'),
      },
      {
        path: '/reset_password',
        name: ROUTE_NAMES.PASSWORD_RESET,
        beforeEnter: (to) => {
          const { isAuthenticated } = useUserStore()

          if (isAuthenticated) {
            return { name: ROUTE_NAMES.PROFILE_PASSWORD_RESET, query: to.query }
          }
          return true
        },
        component: () => import('../views/auth/SetNewPasswordView.vue'),
      },
      {
        path: '/google/login/callback',
        beforeEnter: clearUserInfoMiddleware,
        name: ROUTE_NAMES.GOOGLE_LOGIN_CALLBACK,
        component: () => import('../views/auth/GoogleCallbackView.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: ROUTE_NAMES.DASHBOARD },
  },
  // {
  //   path: '/',
  //   name: ROUTE_NAMES.HOME,
  //   redirect: { name: ROUTE_NAMES.DASHBOARD },
  // },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.meta.mandatoryScrollTop) {
      return { top: 0 }
    }
    if (to.meta.isModal || from.meta.isModal || (to.meta.noScroll && from.meta.noScroll)) {
      return savedPosition || false
    }
    return { top: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const { user } = useUserStore()
  if (!user?.blocked) {
    next()
    return
  }

  const allowedRoutes = [
    ROUTE_NAMES.DEACTIVATED_USER_DASHBOARD,
    ROUTE_NAMES.SALES_CHANNELS,
    ROUTE_NAMES.SUPPORT,
    ROUTE_NAMES.PAYMENT_INFORMATION,
    ROUTE_NAMES.ADMIN,
    ROUTE_NAMES.LOGIN,
  ] as RouteRecordNameGeneric[]
  const toMatched = to.matched.map((item) => item.name)

  if (user?.blocked && !allowedRoutes.some((route) => toMatched.includes(route))) {
    next({ name: ROUTE_NAMES.DEACTIVATED_USER_DASHBOARD })
  } else {
    next()
  }
})

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    window.location.href = to.fullPath
  }
})

export default router
